import { Col, FormInstance, Row } from 'antd';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { IntegrationField } from 'api/integrations';
import {
  JiraConfig,
  JiraEpic,
  JiraIssueType,
  JiraProject,
  MappingMechanism,
  ProjectMappingMechanism,
} from 'api/integrations/jira';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Select } from 'components/atoms/select';
import {
  defaultJiraFieldIds,
  fullWidthItems,
  generateFormFields,
  TableLoader,
} from 'components/molecules/integrations-commons';
import { TaskDescription } from 'components/molecules/tasks/details/description/index';
import { Task } from 'data/tasks';
import { useTranslation } from 'hooks/use-translation';

interface JiraForm {
  form: FormInstance<any>;
  onProjectChange: (projectId: string) => void;
  onIssueTypeChange: (issueTypeId: string) => void;
  onEpicChange: (epicId: string) => void;
  jiraConfig: JiraConfig;
  fieldsList?: IntegrationField[];
  loadingFieldsList?: boolean;
  issueTypes?: JiraIssueType[];
  loadingIssueTypes?: boolean;
  epicsList?: JiraEpic[];
  epicsListForThisProject?: JiraEpic[];
  loadingEpicsList?: boolean;
  jiraProjects?: JiraProject[];
  loadingJiraProjects?: boolean;
  isLoading?: boolean;
  disableEpicSelection?: boolean;
  isFixedProjectMapping?: boolean;
  taskData: Task;
}

type FormValues<T> = Record<'epicId' | 'issuetype' | 'projectId', T>;

export const JiraForm: FC<JiraForm> = ({
  form,
  jiraConfig,
  fieldsList,
  loadingFieldsList,
  issueTypes,
  loadingIssueTypes,
  epicsList,
  epicsListForThisProject,
  loadingEpicsList,
  jiraProjects,
  loadingJiraProjects,
  onProjectChange,
  onEpicChange,
  onIssueTypeChange,
  taskData,
}) => {
  const {
    settingsMeta: {
      fields = [],
      epicMappingMechanism,
      projectMappingMechanism,
      fixedEpicId,
    },
  } = jiraConfig;

  const requiredMessage = useTranslation('input.required');
  const issueTypeLabel = useTranslation('issue_type');
  const epicIdLabel = useTranslation('epic');
  const summaryLabel = useTranslation('summary');
  const descriptionLabel = useTranslation('description');
  const projectLabel = useTranslation('project');
  const hiddenFields = useMemo(
    () =>
      fields
        .filter((field) => !field.isEnabled)
        ?.map((field) => field.integrationFieldId),
    [fields],
  );

  const [allowEpicSelection, setAllowEpicSelection] = useState(true);
  const epicsListOptions = useMemo(() => {
    let _epicsList = epicsList;

    if (projectMappingMechanism === ProjectMappingMechanism.Fixed) {
      switch (epicMappingMechanism) {
        case MappingMechanism.Automatic:
        case MappingMechanism.PromptDuringProjectcreation:
          if (epicsListForThisProject && epicsListForThisProject?.length > 0) {
            _epicsList = epicsListForThisProject;
            setAllowEpicSelection(false);
          }
          break;
        case MappingMechanism.Fixed:
          {
            const fixedEpic = epicsList?.find(
              (_epic) => _epic.id === fixedEpicId,
            );

            if (fixedEpic) {
              _epicsList = [fixedEpic];
              setAllowEpicSelection(false);
            }
          }
          break;
        default:
          break;
      }
    }

    return _epicsList?.map((_epic) => ({
      value: _epic.id,
      label: _epic.name,
    }));
  }, [
    epicMappingMechanism,
    epicsList,
    epicsListForThisProject,
    fixedEpicId,
    projectMappingMechanism,
  ]);

  const epicId = useMemo(() => {
    // if a project is created with JIRA setting to map an epic to a project during project create
    // and if the setting got updated to prompt during issue create
    // select the mapped epic by default.
    if (
      (projectMappingMechanism !== ProjectMappingMechanism.Fixed ||
        (projectMappingMechanism === ProjectMappingMechanism.Fixed &&
          epicMappingMechanism ===
            MappingMechanism.PromptDuringIssuecreation)) &&
      epicsListForThisProject &&
      epicsListForThisProject?.length > 0
    ) {
      return {
        value: epicsListForThisProject[0]?.id,
        label: epicsListForThisProject?.[0]?.name,
      };
    }

    return epicsListOptions?.[0] ?? undefined;
  }, [
    epicsListOptions,
    epicMappingMechanism,
    epicsListForThisProject,
    projectMappingMechanism,
  ]);

  useEffect(() => {
    // set epicId in form instance if it is not set.
    if (!form.getFieldValue('epicId')) {
      form.setFieldsValue({
        ...form.getFieldsValue(),
        epicId,
      });
    }
  }, [epicId, form]);

  return (
    <Form
      layout="vertical"
      form={form}
      scrollToFirstError
      initialValues={{
        epicId,
      }}
      onValuesChange={(
        changedValues: FormValues<{ label: string; value: string }>,
      ) => {
        if (Object.prototype.hasOwnProperty.call(changedValues, 'issuetype')) {
          return onIssueTypeChange(changedValues.issuetype?.value ?? null);
        }
        if (Object.prototype.hasOwnProperty.call(changedValues, 'epicId')) {
          return onEpicChange(changedValues.epicId.value ?? null);
        }
        if (Object.prototype.hasOwnProperty.call(changedValues, 'projectId')) {
          return onProjectChange(changedValues.projectId?.value ?? null);
        }

        return false;
      }}
    >
      <Row gutter={[16, 16]}>
        <Col span={12} hidden={jiraProjects == null}>
          <Form.Item
            name="projectId"
            label={projectLabel}
            rules={[{ required: true, message: requiredMessage }]}
            hidden={jiraProjects == null}
          >
            <Select
              options={(jiraProjects ?? []).map(({ id, name }) => ({
                value: id,
                label: name,
              }))}
              isLoading={loadingJiraProjects}
              isClearable
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name="issuetype"
            label={issueTypeLabel}
            rules={[{ required: true, message: requiredMessage }]}
          >
            <Select
              options={(issueTypes ?? []).map(({ id, name }) => ({
                value: id,
                label: name,
              }))}
              isLoading={loadingIssueTypes}
            />
          </Form.Item>
        </Col>

        {/* Epic */}
        <Col span={12}>
          <Form.Item label={epicIdLabel} name="epicId">
            <Select
              isClearable
              value={epicId}
              options={epicsListOptions}
              isLoading={loadingEpicsList}
              isDisabled={!allowEpicSelection}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            label={summaryLabel}
            name="taskName"
            rules={[{ required: true, message: requiredMessage }]}
          >
            <Input />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item label={descriptionLabel} name="description">
            <TaskDescription.Editor
              taskDescription={form.getFieldValue('description')}
              updateTask={(_taskDescription) =>
                form.setFieldsValue({
                  description: _taskDescription,
                })
              }
            />
          </Form.Item>
        </Col>
        {/* Dynamic Fields */}
        {loadingFieldsList ? (
          <TableLoader />
        ) : (
          (fieldsList ?? [])
            .sort((a, b) =>
              hiddenFields.includes(a.integrationFieldId)
                ? 1
                : hiddenFields.includes(b.integrationFieldId)
                ? -1
                : 0,
            )
            .sort((a, b) =>
              a.required === b.required ? 0 : a.required ? -1 : 1,
            )
            .map(
              (field) =>
                !defaultJiraFieldIds.includes(field.integrationFieldId) && (
                  <Col
                    span={
                      fullWidthItems.includes(field.integrationFieldId)
                        ? 24
                        : 12
                    }
                  >
                    <Form.Item
                      label={
                        <FormattedMessage
                          id={`jira.fields.${field.fieldLabel?.toLowerCase()}`}
                          values={{ label: field.fieldLabel }}
                          defaultMessage={field.fieldLabel}
                        />
                      }
                      name={field.integrationFieldId}
                      rules={[
                        {
                          required: field.required,
                          message: requiredMessage,
                        },
                      ]}
                      initialValue={
                        taskData?.dueDate &&
                        field.integrationFieldId === 'duedate'
                          ? Math.round(new Date(taskData?.dueDate).getTime())
                          : undefined
                      }
                      hidden={hiddenFields.includes(field.integrationFieldId)}
                      getValueFromEvent={(data) => data.valueStr}
                    >
                      {generateFormFields(
                        field,
                        field.metaData?.options ?? [],
                        false,
                        taskData,
                      )}
                    </Form.Item>
                  </Col>
                ),
            )
        )}
      </Row>
    </Form>
  );
};
